import React from "react";
import { 
  CheckCircle2, 
  Smartphone, 
  Stethoscope, 
  Building2, 
  ShieldCheck, 
  Zap, 
  Users, 
  HeartPulse, 
  ArrowRight, 
  Sparkles,
  Award,
  Layers,
  Clock,
  BadgeDollarSign
} from "lucide-react";
import WidgetInfoTooltip from "./WidgetInfoTooltip";

export default function InterventionsSection({ metrics }) {
  return (
    <div
      className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm relative mb-6"
      data-testid="interventions-section"
    >
      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
              <Layers size={12} />
              Modelo Escalonado de Salud Ocupacional
            </span>
            <span className="text-xs text-slate-400">
              3 niveles progresivos de intervención
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <h3 className="text-lg font-bold text-slate-800">
              Matriz Estratégica de Intervenciones & Rutas de Atención
            </h3>
            <WidgetInfoTooltip
              title="Modelo de Intervención en 3 Niveles WE360"
              align="left"
              what="Estrategia corporativa escalonada que clasifica las acciones de salud según el perfil de riesgo clínico del colaborador y la estructura de costos de la empresa."
              calculation="Nivel 1: Coaching digital universal con IA & WhatsApp (100% colaboradores, incluido). Nivel 2: Programas preventivos de estilo de vida con Médico Asignado e IHO (1-2 factores, sin costo adicional). Nivel 3: Marketplace médico especializado para alta complejidad y estudios diagnósticos (3+ factores, bajo demanda o con copago)."
              relevance="Garantiza un retorno de inversión (ROI) óptimo en bienestar corporativo al evitar derivaciones innecesarias a especialistas para personal sano y concentrar los recursos clínicos intensivos en los colaboradores de alto riesgo metabólico y cardiovascular."
            />
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Rutas de cuidado clínico optimizadas para maximizar la prevención sin sobredimensionar el presupuesto asistencial.
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200/60 self-start sm:self-auto">
          <ShieldCheck size={14} className="text-teal-600" />
          <span>Cumplimiento Normativo SST & Medicina Preventiva</span>
        </div>
      </div>

      {/* Grid de los 3 Niveles */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        {/* NIVEL 1: Coaching Digital Universal */}
        <div className="rounded-2xl border border-teal-200 bg-gradient-to-b from-teal-50/50 to-white p-5 flex flex-col justify-between relative shadow-xs">
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-extrabold px-2.5 py-1 rounded-lg bg-teal-100 text-teal-800 border border-teal-200">
                <Smartphone size={13} />
                Nivel 1 • Cobertura Universal
              </span>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                Incluido
              </span>
            </div>

            <h4 className="text-base font-bold text-slate-900 mb-1">
              Coaching Digital con IA & WhatsApp
            </h4>
            <p className="text-xs text-slate-500 mb-4">
              Acompañamiento continuo y monitoreo inteligente para la totalidad de la fuerza laboral.
            </p>

            <div className="space-y-2.5 text-xs text-slate-600">
              <div className="flex items-start gap-2">
                <CheckCircle2 size={15} className="text-teal-600 flex-shrink-0 mt-0.5" />
                <span><strong>Monitoreo rPPG Facial Periódico:</strong> Evaluación óptica rápida del estrés autonómico, variabilidad de frecuencia cardíaca (VFC) y signos vitales.</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 size={15} className="text-teal-600 flex-shrink-0 mt-0.5" />
                <span><strong>Canal WhatsApp Automatizado con IA:</strong> Recordatorios de pausas activas, hidratación, higiene del sueño y cápsulas preventivas.</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 size={15} className="text-teal-600 flex-shrink-0 mt-0.5" />
                <span><strong>Auto-evaluación Continua:</strong> Acceso ilimitado al portal y encuestas de estilo de vida, sedentarismo y bienestar psicosocial.</span>
              </div>
            </div>
          </div>

          <div className="mt-5 pt-3 border-t border-teal-100/80 flex items-center justify-between text-[11px]">
            <span className="text-slate-400 font-medium">Población objetivo:</span>
            <span className="font-bold text-teal-800">100% de la plantilla ({metrics?.totalEmployees || 0} colab.)</span>
          </div>
        </div>

        {/* NIVEL 2: Programas Preventivos & Médico Asignado */}
        <div className="rounded-2xl border border-blue-200 bg-gradient-to-b from-blue-50/50 to-white p-5 flex flex-col justify-between relative shadow-xs">
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-extrabold px-2.5 py-1 rounded-lg bg-blue-100 text-blue-800 border border-blue-200">
                <Stethoscope size={13} />
                Nivel 2 • Riesgo Incipiente
              </span>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                Sin Costo Adicional
              </span>
            </div>

            <h4 className="text-base font-bold text-slate-900 mb-1">
              Programas Preventivos & Médico de Familia
            </h4>
            <p className="text-xs text-slate-500 mb-4">
              Intervención oportuna para colaboradores con 1 a 2 factores modificables presentes.
            </p>

            <div className="space-y-2.5 text-xs text-slate-600">
              <div className="flex items-start gap-2">
                <CheckCircle2 size={15} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <span><strong>Oficial de Salud (IHO) Asignado:</strong> Mentoría personalizada de metas de reducción de cintura y control de tensión arterial.</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 size={15} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <span><strong>Orientación Clínica de Familia:</strong> Teleconsultas de orientación general sin costo adicional para frenar el avance de comorbilidades.</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 size={15} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <span><strong>Planes Nutricionales & Ergonómicos:</strong> Protocolos de intervención temprana para sedentarismo laboral prolongado y estrés agudo.</span>
              </div>
            </div>
          </div>

          <div className="mt-5 pt-3 border-t border-blue-100/80 flex items-center justify-between text-[11px]">
            <span className="text-slate-400 font-medium">Población objetivo:</span>
            <span className="font-bold text-blue-800">
              1 a 2 factores ({((metrics?.concurrence?.c1?.percent || 0) + (metrics?.concurrence?.c2?.percent || 0))}% de la empresa)
            </span>
          </div>
        </div>

        {/* NIVEL 3: Marketplace Especializado */}
        <div className="rounded-2xl border border-purple-200 bg-gradient-to-b from-purple-50/50 to-white p-5 flex flex-col justify-between relative shadow-xs">
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-extrabold px-2.5 py-1 rounded-lg bg-purple-100 text-purple-800 border border-purple-200">
                <Building2 size={13} />
                Nivel 3 • Alta Complejidad
              </span>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                Con Costo / Marketplace
              </span>
            </div>

            <h4 className="text-base font-bold text-slate-900 mb-1">
              Marketplace Médico Clínico Especializado
            </h4>
            <p className="text-xs text-slate-500 mb-4">
              Atención médica de alta especialidad para colaboradores con 3+ factores o patología activa.
            </p>

            <div className="space-y-2.5 text-xs text-slate-600">
              <div className="flex items-start gap-2">
                <CheckCircle2 size={15} className="text-purple-600 flex-shrink-0 mt-0.5" />
                <span><strong>Interconsulta Especializada:</strong> Cardiología, Endocrinología, Psicología clínica y Medicina Interna con agendamiento ágil.</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 size={15} className="text-purple-600 flex-shrink-0 mt-0.5" />
                <span><strong>Exámenes Paraclínicos Asistidos por IA:</strong> Perfil lipídico sérico, HbA1c y electrocardiografía con lectura e interpretación clínica digital.</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 size={15} className="text-purple-600 flex-shrink-0 mt-0.5" />
                <span><strong>Red de Aliados & Cobertura Póliza:</strong> Tarifas corporativas negociadas o facturación directa a la aseguradora de la empresa.</span>
              </div>
            </div>
          </div>

          <div className="mt-5 pt-3 border-t border-purple-100/80 flex items-center justify-between text-[11px]">
            <span className="text-slate-400 font-medium">Población objetivo:</span>
            <span className="font-bold text-purple-800">
              3+ factores ({((metrics?.concurrence?.c3?.percent || 0) + (metrics?.concurrence?.c4Plus?.percent || 0))}% de la empresa)
            </span>
          </div>
        </div>
      </div>

      {/* Tarjeta Informativa de Ahorro y Eficiencia de Costos */}
      <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2.5">
          <BadgeDollarSign size={18} className="text-teal-600 flex-shrink-0" />
          <span className="text-slate-700">
            <strong>Eficiencia Presupuestaria SST:</strong> Al resolver el <strong>80-85%</strong> de las necesidades en Niveles 1 y 2, se mitiga el sobredimensionamiento de pólizas de seguro de salud y se reducen hasta un <strong>40%</strong> los costos de ausentismo médico por descompensaciones crónicas.
          </span>
        </div>
      </div>
    </div>
  );
}
